import { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Tv,
  Plus,
  LayoutGrid,
  List,
  Play,
  Square,
  Trash2,
  ArrowLeft,
  Clock,
  Radio,
  ChevronRight,
  X,
  Zap,
  CalendarClock,
  Settings2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { channelsApi, sourcesApi, contentApi, epgApi } from '@/api/client';
import type { Channel, ChannelStatus, ChannelType, FastChannelConfig, ScheduleEntry, ScheduleSourceType, EPGEntry } from '@/types';

// ---- Health Indicator (3-dot) ----

function HealthIndicator({ status }: { status: ChannelStatus }) {
  const dotColor = (threshold: ChannelStatus[]): string => {
    if (threshold.includes(status)) return 'bg-status-healthy';
    if (status === 'error') return 'bg-status-error';
    if (status === 'starting' || status === 'stopping') return 'bg-status-warning';
    return 'bg-silver-muted/30';
  };

  return (
    <div className="flex items-center gap-1">
      <span
        className={cn(
          'w-2 h-2 rounded-full transition-colors',
          dotColor(['live', 'ad_break', 'starting', 'stopping'])
        )}
      />
      <span
        className={cn(
          'w-2 h-2 rounded-full transition-colors',
          dotColor(['live', 'ad_break', 'starting'])
        )}
      />
      <span
        className={cn(
          'w-2 h-2 rounded-full transition-colors',
          dotColor(['live', 'ad_break'])
        )}
      />
    </div>
  );
}

// ---- Status Badge ----

function StatusBadge({ status }: { status: ChannelStatus }) {
  const colors: Record<ChannelStatus, string> = {
    idle: 'bg-silver-muted/20 text-silver-muted',
    starting: 'bg-status-warning/20 text-status-warning',
    live: 'bg-status-healthy/20 text-status-healthy',
    ad_break: 'bg-gold/20 text-gold',
    stopping: 'bg-status-warning/20 text-status-warning',
    error: 'bg-status-error/20 text-status-error',
  };

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[status])}>
      {status.replace('_', ' ')}
    </span>
  );
}

// ---- Channel Card ----

function ChannelCard({ channel }: { channel: Channel }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/streaming/channels/${channel.id}`)}
      className="bg-surface border border-surface-elevated rounded-xl p-5 cursor-pointer hover:border-gold/30 transition-colors group"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-surface-elevated flex items-center justify-center">
            <Tv size={18} className="text-gold" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-silver-bright group-hover:text-gold-light transition-colors">
              {channel.name}
            </h3>
            <p className="text-xs text-silver-muted font-mono">{channel.id}</p>
          </div>
        </div>
        <HealthIndicator status={channel.status} />
      </div>
      {channel.description && (
        <p className="text-xs text-silver-muted mb-3 line-clamp-2">{channel.description}</p>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <StatusBadge status={channel.status} />
          {channel.channel_type === 'fast' && (
            <span className="px-2 py-0.5 rounded-full bg-gold/20 text-gold text-[10px] font-bold flex items-center gap-1">
              <Zap size={10} />
              FAST
            </span>
          )}
        </div>
        <ChevronRight size={14} className="text-silver-muted group-hover:text-gold transition-colors" />
      </div>
    </div>
  );
}

// ---- Channel List Row ----

function ChannelRow({ channel }: { channel: Channel }) {
  const navigate = useNavigate();

  return (
    <div
      onClick={() => navigate(`/streaming/channels/${channel.id}`)}
      className="flex items-center justify-between py-3 px-4 border-b border-surface-elevated last:border-b-0 hover:bg-surface-elevated/50 cursor-pointer transition-colors"
    >
      <div className="flex items-center gap-3">
        <HealthIndicator status={channel.status} />
        <div>
          <span className="text-sm font-medium text-silver-bright">{channel.name}</span>
          <span className="text-xs text-silver-muted ml-2">{channel.description}</span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {channel.currentEvent && (
          <span className="text-xs text-silver-muted">{channel.currentEvent.title}</span>
        )}
        {channel.channel_type === 'fast' && (
          <span className="px-2 py-0.5 rounded-full bg-gold/20 text-gold text-[10px] font-bold flex items-center gap-1">
            <Zap size={10} />
            FAST
          </span>
        )}
        <StatusBadge status={channel.status} />
      </div>
    </div>
  );
}

// ---- Create Channel Dialog ----

interface CreateChannelDialogProps {
  open: boolean;
  onClose: () => void;
}

function CreateChannelDialog({ open, onClose }: CreateChannelDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hlsEndpoint, setHlsEndpoint] = useState('');
  const [dashEndpoint, setDashEndpoint] = useState('');
  const [channelType, setChannelType] = useState<ChannelType>('linear');
  const [fastCategories, setFastCategories] = useState<string[]>([]);
  const [scheduleLookahead, setScheduleLookahead] = useState('6');
  const [adBreakInterval, setAdBreakInterval] = useState('20');
  const [adBreakDurationSec, setAdBreakDurationSec] = useState('120');
  const [maxRepeatHours, setMaxRepeatHours] = useState('24');
  const [autoSchedule, setAutoSchedule] = useState(true);

  const { data: categories } = useQuery({
    queryKey: ['content-categories'],
    queryFn: contentApi.getCategories,
    enabled: open && channelType === 'fast',
  });

  const createMutation = useMutation({
    mutationFn: () => {
      const fastConfig: FastChannelConfig | undefined =
        channelType === 'fast'
          ? {
              categories: fastCategories,
              scheduleLookaheadHours: Number(scheduleLookahead),
              adBreakIntervalMinutes: Number(adBreakInterval),
              adBreakDurationMs: Number(adBreakDurationSec) * 1000,
              maxRepeatWithinHours: Number(maxRepeatHours),
              autoScheduleEnabled: autoSchedule,
            }
          : undefined;

      return channelsApi.create({
        name,
        description,
        config: {
          hlsEndpoint: hlsEndpoint || undefined,
          dashEndpoint: dashEndpoint || undefined,
        },
        channel_type: channelType,
        fast_config: fastConfig,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      resetAndClose();
    },
  });

  function resetAndClose() {
    setName('');
    setDescription('');
    setHlsEndpoint('');
    setDashEndpoint('');
    setChannelType('linear');
    setFastCategories([]);
    setScheduleLookahead('6');
    setAdBreakInterval('20');
    setAdBreakDurationSec('120');
    setMaxRepeatHours('24');
    setAutoSchedule(true);
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-lg font-semibold text-silver-bright">Create Channel</h3>
          <button onClick={resetAndClose} className="text-silver-muted hover:text-silver-bright">
            <X size={18} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="p-6 space-y-4"
        >
          {/* Channel Type Toggle */}
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Channel Type</label>
            <div className="flex items-center bg-surface-deep border border-surface-elevated rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setChannelType('linear')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  channelType === 'linear'
                    ? 'bg-gold/20 text-gold'
                    : 'text-silver-muted hover:text-silver-bright',
                )}
              >
                Linear
              </button>
              <button
                type="button"
                onClick={() => setChannelType('fast')}
                className={cn(
                  'flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1',
                  channelType === 'fast'
                    ? 'bg-gold/20 text-gold'
                    : 'text-silver-muted hover:text-silver-bright',
                )}
              >
                <Zap size={12} />
                FAST
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="My Channel"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="Channel description"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">HLS Endpoint</label>
            <input
              value={hlsEndpoint}
              onChange={(e) => setHlsEndpoint(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50 font-mono"
              placeholder="https://cdn.example.com/channel-1/master.m3u8"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">DASH Endpoint</label>
            <input
              value={dashEndpoint}
              onChange={(e) => setDashEndpoint(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50 font-mono"
              placeholder="https://cdn.example.com/channel-1/manifest.mpd"
            />
          </div>

          {/* FAST Config Fields */}
          {channelType === 'fast' && (
            <div className="space-y-4 pt-2 border-t border-surface-elevated">
              <div className="flex items-center gap-2 text-gold text-xs font-semibold">
                <Zap size={12} />
                FAST Configuration
              </div>
              <div>
                <label className="block text-xs font-medium text-silver-muted mb-1.5">
                  Categories
                </label>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {fastCategories.map((cat) => (
                    <span
                      key={cat}
                      className="px-2 py-0.5 rounded-full bg-gold/10 text-gold text-xs flex items-center gap-1"
                    >
                      {cat}
                      <button
                        type="button"
                        onClick={() => setFastCategories((prev) => prev.filter((c) => c !== cat))}
                        className="hover:text-gold-light"
                      >
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !fastCategories.includes(e.target.value)) {
                      setFastCategories((prev) => [...prev, e.target.value]);
                    }
                  }}
                  className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                >
                  <option value="">Add a category...</option>
                  {categories
                    ?.filter((c) => !fastCategories.includes(c))
                    .map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-silver-muted mb-1.5">
                    Lookahead (hours)
                  </label>
                  <input
                    type="number"
                    value={scheduleLookahead}
                    onChange={(e) => setScheduleLookahead(e.target.value)}
                    min={1}
                    max={72}
                    className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-silver-muted mb-1.5">
                    Ad Break Interval (min)
                  </label>
                  <input
                    type="number"
                    value={adBreakInterval}
                    onChange={(e) => setAdBreakInterval(e.target.value)}
                    min={5}
                    max={120}
                    className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-silver-muted mb-1.5">
                    Ad Break Duration (sec)
                  </label>
                  <input
                    type="number"
                    value={adBreakDurationSec}
                    onChange={(e) => setAdBreakDurationSec(e.target.value)}
                    min={15}
                    max={600}
                    className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-silver-muted mb-1.5">
                    Max Repeat (hours)
                  </label>
                  <input
                    type="number"
                    value={maxRepeatHours}
                    onChange={(e) => setMaxRepeatHours(e.target.value)}
                    min={1}
                    max={168}
                    className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={autoSchedule}
                  onChange={(e) => setAutoSchedule(e.target.checked)}
                  className="rounded border-surface-elevated bg-surface-deep text-gold focus:ring-gold/50"
                />
                <span className="text-sm text-silver-bright">Auto-schedule enabled</span>
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetAndClose}
              className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name || createMutation.isPending}
              className="px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Channel'}
            </button>
          </div>
          {createMutation.isError && (
            <p className="text-xs text-status-error">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create channel'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

// ---- Schedule Entry Row ----

function ScheduleRow({ entry, onDelete }: { entry: ScheduleEntry; onDelete: () => void }) {
  const sourceTypeLabel: Record<ScheduleSourceType, string> = {
    cmaf_live: 'CMAF Live',
    vod: 'VOD',
    ad_break: 'Ad Break',
    slate: 'Slate',
  };

  const sourceTypeColor: Record<ScheduleSourceType, string> = {
    cmaf_live: 'text-status-healthy',
    vod: 'text-gold',
    ad_break: 'text-status-warning',
    slate: 'text-silver-muted',
  };

  const statusColor: Record<string, string> = {
    scheduled: 'text-silver-muted',
    playing: 'text-status-healthy',
    completed: 'text-gold-dark',
    skipped: 'text-silver-muted/50',
  };

  return (
    <div className="flex items-center justify-between py-2.5 px-4 border-b border-surface-elevated last:border-b-0">
      <div className="flex items-center gap-4">
        <div className="text-xs font-mono text-silver-muted w-20">
          {new Date(entry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="text-xs font-mono text-silver-muted w-16">
          {(entry.durationMs / 1000).toFixed(0)}s
        </div>
        <span className={cn('text-xs font-medium w-20', sourceTypeColor[entry.sourceType])}>
          {sourceTypeLabel[entry.sourceType]}
        </span>
        <span className="text-sm text-silver-bright">{entry.title}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className={cn('text-xs', statusColor[entry.status])}>{entry.status}</span>
        <button
          onClick={onDelete}
          className="text-silver-muted hover:text-status-error transition-colors"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ---- Add Schedule Entry Form ----

function AddScheduleForm({ channelId }: { channelId: string }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [sourceType, setSourceType] = useState<ScheduleSourceType>('cmaf_live');
  const [sourceRef, setSourceRef] = useState('');
  const [startTime, setStartTime] = useState('');
  const [durationSec, setDurationSec] = useState('');

  const { data: sources } = useQuery({
    queryKey: ['sources'],
    queryFn: sourcesApi.list,
    enabled: sourceType === 'cmaf_live',
  });

  const addMutation = useMutation({
    mutationFn: () =>
      channelsApi.addScheduleEntry(channelId, {
        title,
        sourceType,
        sourceRef: sourceType === 'cmaf_live' ? sourceRef : undefined,
        startTime: startTime ? new Date(startTime).toISOString() : new Date().toISOString(),
        durationMs: Number(durationSec) * 1000,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
      queryClient.invalidateQueries({ queryKey: ['schedule', channelId] });
      setTitle('');
      setSourceRef('');
      setDurationSec('');
      setStartTime('');
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        addMutation.mutate();
      }}
      className="space-y-3 p-4 bg-surface-deep rounded-lg"
    >
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs text-silver-muted mb-1">Title</label>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
            placeholder="Schedule entry title"
          />
        </div>
        <div className="w-32">
          <label className="block text-xs text-silver-muted mb-1">Source Type</label>
          <select
            value={sourceType}
            onChange={(e) => {
              setSourceType(e.target.value as ScheduleSourceType);
              setSourceRef('');
            }}
            className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
          >
            <option value="cmaf_live">CMAF Live</option>
            <option value="vod">VOD</option>
            <option value="ad_break">Ad Break</option>
            <option value="slate">Slate</option>
          </select>
        </div>
        <div className="w-44">
          <label className="block text-xs text-silver-muted mb-1">Start Time</label>
          <input
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
          />
        </div>
        <div className="w-24">
          <label className="block text-xs text-silver-muted mb-1">Duration (s)</label>
          <input
            type="number"
            value={durationSec}
            onChange={(e) => setDurationSec(e.target.value)}
            required
            min={1}
            className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
            placeholder="30"
          />
        </div>
        <button
          type="submit"
          disabled={!title || !durationSec || addMutation.isPending}
          className="px-4 py-1.5 bg-gold text-surface-deep text-sm font-medium rounded hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
        >
          {addMutation.isPending ? 'Adding...' : 'Add'}
        </button>
      </div>

      {sourceType === 'cmaf_live' && (
        <div>
          <label className="block text-xs text-silver-muted mb-1">Source</label>
          <select
            value={sourceRef}
            onChange={(e) => setSourceRef(e.target.value)}
            className="w-full bg-surface border border-surface-elevated rounded px-2.5 py-1.5 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
          >
            <option value="">Select a source...</option>
            {/* Retired sources (JSS#1151) are no AirChain's any more: not offered. */}
            {sources?.filter((s) => s.status !== 'retired' || s.id === sourceRef).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.type.toUpperCase()})
              </option>
            ))}
          </select>
        </div>
      )}
    </form>
  );
}

// ---- EPG Timeline ----

function EPGTimeline({ channelId }: { channelId: string }) {
  const [zoomHours, setZoomHours] = useState(12);
  const [hoveredEntry, setHoveredEntry] = useState<EPGEntry | null>(null);

  const { data: guide } = useQuery({
    queryKey: ['epg', channelId, zoomHours],
    queryFn: () => epgApi.getGuide(channelId, zoomHours),
    refetchInterval: 30_000,
  });

  const { timelineStart, pixelsPerMs, entries, hourMarkers, nowPosition } = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setMinutes(0, 0, 0);
    const totalMs = zoomHours * 60 * 60 * 1000;
    const pxPerMs = 1 / (totalMs / 1200); // 1200px total width

    const markers: Date[] = [];
    for (let i = 0; i <= zoomHours; i++) {
      const t = new Date(start.getTime() + i * 60 * 60 * 1000);
      markers.push(t);
    }

    const nowPos = (now.getTime() - start.getTime()) * pxPerMs;

    return {
      timelineStart: start,
      pixelsPerMs: pxPerMs,
      entries: guide?.entries ?? [],
      hourMarkers: markers,
      nowPosition: nowPos,
    };
  }, [guide, zoomHours]);

  const entryColor: Record<string, string> = {
    vod: 'bg-gold/60 hover:bg-gold/80 border-gold/80',
    ad_break: 'bg-status-error/40 hover:bg-status-error/60 border-status-error/60',
    slate: 'bg-silver-muted/30 hover:bg-silver-muted/50 border-silver-muted/50',
  };

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl">
      <div className="flex items-center justify-between px-5 py-4 border-b border-surface-elevated">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-gold" />
          <h3 className="text-sm font-semibold text-silver-bright">EPG Timeline</h3>
          <span className="text-xs text-silver-muted">({entries.length} entries)</span>
        </div>
        <div className="flex items-center gap-1">
          {[4, 12, 24].map((h) => (
            <button
              key={h}
              onClick={() => setZoomHours(h)}
              className={cn(
                'px-2.5 py-1 text-xs font-medium rounded transition-colors',
                zoomHours === h
                  ? 'bg-gold/20 text-gold'
                  : 'text-silver-muted hover:text-silver-bright',
              )}
            >
              {h}h
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative overflow-x-auto px-5 py-4">
        <div className="relative" style={{ width: 1200, height: 80 }}>
          {/* Hour markers */}
          {hourMarkers.map((marker) => {
            const x = (marker.getTime() - timelineStart.getTime()) * pixelsPerMs;
            return (
              <div
                key={marker.toISOString()}
                className="absolute top-0 text-[10px] text-silver-muted font-mono"
                style={{ left: x }}
              >
                <div className="w-px h-full bg-surface-elevated absolute top-4" style={{ height: 60 }} />
                {marker.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            );
          })}

          {/* Entries */}
          {entries.map((entry) => {
            const start = new Date(entry.startTime).getTime() - timelineStart.getTime();
            const x = Math.max(0, start * pixelsPerMs);
            const w = Math.max(2, entry.durationMs * pixelsPerMs);

            return (
              <div
                key={entry.id}
                className={cn(
                  'absolute rounded border cursor-default transition-colors',
                  entryColor[entry.sourceType] ?? 'bg-silver-muted/20',
                )}
                style={{ left: x, top: 20, width: w, height: 40 }}
                onMouseEnter={() => setHoveredEntry(entry)}
                onMouseLeave={() => setHoveredEntry(null)}
              >
                {w > 40 && (
                  <div className="px-1.5 py-1 text-[10px] text-silver-bright truncate leading-tight">
                    {entry.title}
                  </div>
                )}
              </div>
            );
          })}

          {/* Now line */}
          {nowPosition > 0 && nowPosition < 1200 && (
            <div
              className="absolute top-4 w-0.5 bg-status-error z-10"
              style={{ left: nowPosition, height: 60 }}
            />
          )}
        </div>
      </div>

      {/* Hover tooltip */}
      {hoveredEntry && (
        <div className="px-5 pb-3 flex items-center gap-4 text-xs text-silver-muted">
          <span className="font-medium text-silver-bright">{hoveredEntry.title}</span>
          <span>
            {new Date(hoveredEntry.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {' - '}
            {new Date(hoveredEntry.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span className={cn(
            'px-1.5 py-0.5 rounded text-[10px] font-medium',
            hoveredEntry.sourceType === 'vod' ? 'bg-gold/20 text-gold' :
            hoveredEntry.sourceType === 'ad_break' ? 'bg-status-error/20 text-status-error' :
            'bg-silver-muted/20 text-silver-muted',
          )}>
            {hoveredEntry.sourceType.replace('_', ' ')}
          </span>
        </div>
      )}

      {/* Legend */}
      <div className="px-5 pb-4 flex items-center gap-4">
        <div className="flex items-center gap-1.5 text-[10px] text-silver-muted">
          <div className="w-3 h-3 rounded bg-gold/60" />
          VOD
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-silver-muted">
          <div className="w-3 h-3 rounded bg-status-error/40" />
          Ad Break
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-silver-muted">
          <div className="w-3 h-3 rounded bg-silver-muted/30" />
          Slate
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-silver-muted">
          <div className="w-0.5 h-3 bg-status-error" />
          Now
        </div>
      </div>
    </div>
  );
}

// ---- Channel Detail ----

function ChannelDetail({ channelId }: { channelId: string }) {
  const queryClient = useQueryClient();

  const { data: channel, isLoading, error } = useQuery({
    queryKey: ['channel', channelId],
    queryFn: () => channelsApi.get(channelId),
    refetchInterval: 5_000,
  });

  const { data: schedule } = useQuery({
    queryKey: ['schedule', channelId],
    queryFn: () => channelsApi.getSchedule(channelId),
    refetchInterval: 5_000,
  });

  const startMutation = useMutation({
    mutationFn: () => channelsApi.start(channelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel', channelId] }),
  });

  const stopMutation = useMutation({
    mutationFn: () => channelsApi.stop(channelId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channel', channelId] }),
  });

  const deleteEntryMutation = useMutation({
    mutationFn: (entryId: string) => channelsApi.deleteScheduleEntry(channelId, entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
      queryClient.invalidateQueries({ queryKey: ['schedule', channelId] });
    },
  });

  const fillMutation = useMutation({
    mutationFn: () => channelsApi.triggerFastFill(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channel', channelId] });
      queryClient.invalidateQueries({ queryKey: ['schedule', channelId] });
      queryClient.invalidateQueries({ queryKey: ['epg', channelId] });
    },
  });

  if (isLoading) {
    return <div className="text-silver-muted text-sm">Loading channel...</div>;
  }

  if (error || !channel) {
    return <div className="text-status-error text-sm">Failed to load channel</div>;
  }

  const scheduleEntries = schedule ?? channel.schedule ?? [];
  const canStart = channel.status === 'idle' || channel.status === 'error';
  const canStop = channel.status === 'live' || channel.status === 'ad_break';
  const isFast = channel.channel_type === 'fast';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/streaming/channels"
            className="text-silver-muted hover:text-silver-bright transition-colors"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h2 className="text-xl font-bold text-silver-bright">{channel.name}</h2>
            <p className="text-xs text-silver-muted font-mono">{channel.id}</p>
          </div>
          <HealthIndicator status={channel.status} />
          <StatusBadge status={channel.status} />
          {isFast && (
            <span className="px-2.5 py-0.5 rounded-full bg-gold/20 text-gold text-xs font-bold flex items-center gap-1">
              <Zap size={12} />
              FAST
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isFast && (
            <button
              onClick={() => fillMutation.mutate()}
              disabled={fillMutation.isPending}
              className="flex items-center gap-1.5 px-4 py-2 bg-gold/20 text-gold text-sm font-medium rounded-lg hover:bg-gold/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <CalendarClock size={14} />
              {fillMutation.isPending ? 'Filling...' : 'Fill Schedule'}
            </button>
          )}
          <button
            onClick={() => startMutation.mutate()}
            disabled={!canStart || startMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-status-healthy/20 text-status-healthy text-sm font-medium rounded-lg hover:bg-status-healthy/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Play size={14} />
            Start
          </button>
          <button
            onClick={() => stopMutation.mutate()}
            disabled={!canStop || stopMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 bg-status-error/20 text-status-error text-sm font-medium rounded-lg hover:bg-status-error/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <Square size={14} />
            Stop
          </button>
        </div>
      </div>

      {/* Channel Info */}
      <div className={cn('grid gap-6', isFast ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1 lg:grid-cols-2')}>
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">Channel Info</h3>
          {channel.description && (
            <p className="text-sm text-silver-muted mb-4">{channel.description}</p>
          )}
          <div className="space-y-3">
            <InfoRow label="Type" value={isFast ? 'FAST' : 'Linear'} />
            <InfoRow label="HLS Endpoint" value={channel.config.hlsEndpoint} mono />
            <InfoRow label="DASH Endpoint" value={channel.config.dashEndpoint} mono />
            <InfoRow label="Segment Duration" value={channel.config.segmentDurationMs != null ? `${channel.config.segmentDurationMs}ms` : ''} />
            <InfoRow label="Window Size" value={channel.config.windowSize != null ? String(channel.config.windowSize) : ''} />
          </div>
        </div>

        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <h3 className="text-sm font-semibold text-silver-bright mb-4">Current State</h3>
          <div className="space-y-3">
            <InfoRow label="Status" value={channel.status} />
            {channel.currentEvent && (
              <>
                <InfoRow label="Current Event" value={channel.currentEvent.title} />
                <InfoRow label="Event Type" value={channel.currentEvent.type} />
              </>
            )}
            {channel.nextEvent && (
              <InfoRow label="Next Event" value={channel.nextEvent.title} />
            )}
            <InfoRow label="Created" value={channel.createdAt ? new Date(channel.createdAt).toLocaleString() : ''} />
            <InfoRow label="Updated" value={channel.updatedAt ? new Date(channel.updatedAt).toLocaleString() : ''} />
          </div>
        </div>

        {/* FAST Config Info Card */}
        {isFast && channel.fast_config && (
          <div className="bg-surface border border-surface-elevated rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Settings2 size={14} className="text-gold" />
              <h3 className="text-sm font-semibold text-silver-bright">FAST Config</h3>
            </div>
            <div className="space-y-3">
              <InfoRow label="Categories" value={channel.fast_config.categories.join(', ') || 'None'} />
              <InfoRow label="Lookahead" value={`${channel.fast_config.scheduleLookaheadHours}h`} />
              <InfoRow label="Ad Interval" value={`${channel.fast_config.adBreakIntervalMinutes} min`} />
              <InfoRow label="Ad Duration" value={`${channel.fast_config.adBreakDurationMs / 1000}s`} />
              <InfoRow label="Max Repeat" value={`${channel.fast_config.maxRepeatWithinHours}h`} />
              <InfoRow label="Auto-Schedule" value={channel.fast_config.autoScheduleEnabled ? 'Enabled' : 'Disabled'} />
            </div>
          </div>
        )}
      </div>

      {/* EPG Timeline (FAST channels only) */}
      {isFast && <EPGTimeline channelId={channelId} />}

      {/* Schedule */}
      <div className="bg-surface border border-surface-elevated rounded-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-surface-elevated">
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-gold" />
            <h3 className="text-sm font-semibold text-silver-bright">Schedule</h3>
            <span className="text-xs text-silver-muted">({scheduleEntries.length} entries)</span>
          </div>
        </div>

        {/* Schedule entries */}
        {scheduleEntries.length > 0 ? (
          <div className="max-h-72 overflow-y-auto">
            {scheduleEntries.map((entry) => (
              <ScheduleRow
                key={entry.id}
                entry={entry}
                onDelete={() => deleteEntryMutation.mutate(entry.id)}
              />
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-silver-muted text-sm">No schedule entries</div>
        )}

        {/* Add schedule entry */}
        <div className="border-t border-surface-elevated">
          <AddScheduleForm channelId={channelId} />
        </div>
      </div>
    </div>
  );
}

// ---- Info Row ----

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs text-silver-muted shrink-0">{label}</span>
      <span
        className={cn(
          'text-sm text-silver-bright text-right break-all',
          mono && 'font-mono text-xs'
        )}
      >
        {value || '--'}
      </span>
    </div>
  );
}

// ---- Channel List View ----

function ChannelListView() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [createOpen, setCreateOpen] = useState(false);

  const { data: channels, isLoading, error } = useQuery({
    queryKey: ['channels'],
    queryFn: channelsApi.list,
    refetchInterval: 10_000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-silver-bright">Channels</h2>
          <p className="text-sm text-silver-muted mt-1">
            Manage live channels and schedules
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-surface border border-surface-elevated rounded-lg">
            <button
              onClick={() => setViewMode('grid')}
              className={cn(
                'p-2 rounded-l-lg transition-colors',
                viewMode === 'grid'
                  ? 'bg-gold/10 text-gold'
                  : 'text-silver-muted hover:text-silver-bright'
              )}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={cn(
                'p-2 rounded-r-lg transition-colors',
                viewMode === 'list'
                  ? 'bg-gold/10 text-gold'
                  : 'text-silver-muted hover:text-silver-bright'
              )}
            >
              <List size={16} />
            </button>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light transition-colors"
          >
            <Plus size={16} />
            New Channel
          </button>
        </div>
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-silver-muted text-sm">Loading channels...</div>
      )}
      {error && !channels && (
        <div className="text-center py-12 text-status-error text-sm">Failed to load channels</div>
      )}
      {channels && channels.length === 0 && (
        <div className="text-center py-16 space-y-3">
          <Radio size={40} className="mx-auto text-silver-muted/30" />
          <p className="text-silver-muted">No channels yet</p>
          <button
            onClick={() => setCreateOpen(true)}
            className="text-gold text-sm hover:text-gold-light transition-colors"
          >
            Create your first channel
          </button>
        </div>
      )}

      {channels && channels.length > 0 && viewMode === 'grid' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {channels.map((channel) => (
            <ChannelCard key={channel.id} channel={channel} />
          ))}
        </div>
      )}

      {channels && channels.length > 0 && viewMode === 'list' && (
        <div className="bg-surface border border-surface-elevated rounded-xl">
          {channels.map((channel) => (
            <ChannelRow key={channel.id} channel={channel} />
          ))}
        </div>
      )}

      <CreateChannelDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

// ---- Main Export ----

export default function Channels() {
  const { channelId } = useParams<{ channelId: string }>();

  if (channelId) {
    return <ChannelDetail channelId={channelId} />;
  }

  return <ChannelListView />;
}
